import React, { useState, useEffect } from 'react';
import { X, MapPin, Loader2, Building2, School, BookOpen, Warehouse, Building } from 'lucide-react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';

export interface LocationData {
    id: string;
    nama: string;
    latitude: number;
    longitude: number;
    radius: number;
    type: string;
    geofence_type?: 'radius' | 'polygon';
    polygon_coordinates?: { lat: number; lng: number }[];
}

interface PortalGuruModalPilihanLokasiProps {
    onSelect: (location: LocationData) => void;
    onClose: () => void;
}

export const PortalGuruModalPilihanLokasi: React.FC<PortalGuruModalPilihanLokasiProps> = ({ onSelect, onClose }) => {
    const [locations, setLocations] = useState<LocationData[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        const q = query(collection(db, 'lokasi_presensi'), orderBy('nama', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const locs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as LocationData[];
            setLocations(locs);
            setLoading(false);

            // Auto-select first if available
            // if (locs.length > 0) setSelectedId(locs[0].id);
        });
        return () => unsubscribe();
    }, []);

    const getIcon = (type: string) => {
        switch (type) {
            case 'child_care': return <Building2 size={24} />;
            case 'school': return <School size={24} />;
            case 'menu_book': return <BookOpen size={24} />;
            case 'mosque': return <Warehouse size={24} />; // Using Warehouse as Mosque placeholder or import another icon
            case 'apartment': return <Building size={24} />;
            default: return <MapPin size={24} />;
        }
    };

    const getColor = (type: string) => {
        switch (type) {
            case 'child_care': return 'bg-pink-100 text-pink-600';
            case 'school': return 'bg-blue-100 text-blue-600';
            case 'menu_book': return 'bg-emerald-100 text-emerald-600';
            case 'mosque': return 'bg-teal-100 text-teal-600';
            case 'apartment': return 'bg-orange-100 text-orange-600';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const handleConfirm = () => {
        if (!selectedId) return;
        const loc = locations.find(l => l.id === selectedId);
        if (loc) onSelect(loc);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center px-4 pb-6 sm:p-0">
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
            <div className="relative w-full max-w-sm bg-white rounded-3xl p-5 shadow-2xl ring-1 ring-white/10 transform transition-all animate-in slide-in-from-bottom-10 fade-in duration-300 max-h-[80vh] flex flex-col">
                <div className="absolute top-4 right-4 z-10">
                    <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition-colors">
                        <X size={20} />
                    </button>
                </div>
                <div className="text-center mb-6 mt-1 shrink-0">
                    <h3 className="text-xl font-bold text-[#111813] tracking-tight">Pilih Lokasi Kerja</h3>
                    <p className="text-xs text-[#63886f] mt-1">Silahkan pilih lokasi kerja Anda</p>
                </div>

                <div className="flex flex-col gap-3 mb-6 overflow-y-auto pr-1 -mr-2 scrollbar-thin scrollbar-thumb-gray-200">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="animate-spin text-[#40513B]" />
                        </div>
                    ) : locations.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm py-4">Belum ada lokasi presensi.</p>
                    ) : (
                        locations.map(loc => (
                            <div
                                key={loc.id}
                                className={`relative group cursor-pointer`}
                                onClick={() => setSelectedId(loc.id)}
                            >
                                <div className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all cursor-pointer ${selectedId === loc.id ? 'bg-[#40513B]/5 border-[#40513B] shadow-sm' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`size-10 rounded-full flex items-center justify-center ${getColor(loc.type)}`}>
                                            {getIcon(loc.type)}
                                        </div>
                                        <div className="flex flex-col text-left">
                                            <span className="text-sm font-bold text-[#111813]">{loc.nama}</span>
                                            <span className="text-xs font-medium text-[#63886f]">{loc.geofence_type === 'polygon' ? 'Area Polygon' : `Radius: ${loc.radius}m`}</span>
                                        </div>
                                    </div>
                                    <div className={`size-5 rounded-full border-2 flex items-center justify-center transition-colors ${selectedId === loc.id ? 'border-[#40513B] bg-[#40513B]' : 'border-gray-300'}`}>
                                        <div className={`size-2 bg-white rounded-full transition-opacity ${selectedId === loc.id ? 'opacity-100' : 'opacity-0'}`}></div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <button
                    onClick={handleConfirm}
                    disabled={!selectedId}
                    className={`w-full py-3.5 font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 ${selectedId ? 'bg-[#40513B] hover:bg-[#344230] text-white shadow-[#40513B]/25' : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'}`}
                >
                    Konfirmasi Lokasi
                </button>
            </div>
        </div>
    );
};
